import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type NoAccessReason = 'superadmin' | 'tenantAdmin' | 'fleet' | 'complianceTeam' | 'cockpit';

const REQUIREMENT: Record<NoAccessReason, { key: string; fallback: string }> = {
  superadmin: {
    key: 'errors.noAccess.needSuperadmin',
    fallback: 'Diese Seite ist dem Plattform-Betrieb (Superadmin) vorbehalten.',
  },
  tenantAdmin: {
    key: 'errors.noAccess.needTenantAdmin',
    fallback: 'Diese Seite ist für Mandanten-Administratoren.',
  },
  fleet: {
    key: 'errors.noAccess.needFleetRole',
    fallback: 'Diese Seite ist für den Flottenbetrieb: Superadmin oder eine Flotten-Rolle.',
  },
  complianceTeam: {
    key: 'errors.noAccess.needComplianceTeam',
    fallback: 'Diese Seite ist für Mandanten-Administratoren, Compliance-Verantwortliche und Prüfer/innen.',
  },
  cockpit: {
    key: 'errors.noAccess.needCockpitRole',
    fallback: 'Diese Seite ist für Mandanten-Administratoren und die Geschäftsführung.',
  },
};

// Platform and fleet roles are not granted inside a tenant, so asking the
// tenant's administration for them would lead nowhere.
const NEXT_STEP: Record<'platform' | 'tenant', { key: string; fallback: string }> = {
  platform: {
    key: 'errors.noAccess.nextStepPlatform',
    fallback:
      'Diese Seite gehört zum Plattformbetrieb und lässt sich nicht über eine Rolle in Ihrem Mandanten freischalten. Was Sie verwalten dürfen, finden Sie im Menü dieser Konsole; Ihre Arbeitsbereiche wie Chat, Dokumente und Compliance finden Sie im Portal.',
  },
  tenant: {
    key: 'errors.noAccess.nextStep',
    fallback:
      'Wenn Sie diese Seite für Ihre Arbeit brauchen, bitten Sie Ihre Administration um die passende Rolle. Ihre Arbeitsbereiche wie Chat, Dokumente und Compliance finden Sie im Portal.',
  },
};

/**
 * The tenant portal for this console: admin.<domain> → <domain>,
 * admin-<box>.<domain> → <box>.<domain>. Null when it cannot be derived
 * (local dev with a port, unknown host scheme).
 */
export function derivePortalUrl(location: Pick<Location, 'protocol' | 'host'>): string | null {
  const host = location.host.toLowerCase();
  if (host.includes(':')) return null;
  for (const prefix of ['admin.', 'admin-']) {
    if (host.startsWith(prefix) && host.length > prefix.length) {
      return `${location.protocol}//${host.slice(prefix.length)}/access`;
    }
  }
  return null;
}

/**
 * The portal entry for this console: derived from the console host, else the
 * configured portal (VITE_APP_URL). /access forwards a signed-in person to
 * /{tenant}, the portal of their own company.
 */
export function resolvePortalUrl(): string | null {
  if (typeof window !== 'undefined') {
    const derived = derivePortalUrl(window.location);
    if (derived) return derived;
  }
  const configured = String(import.meta.env.VITE_APP_URL || '').trim().replace(/\/$/, '');
  return configured ? `${configured}/access` : null;
}

export default function NoAccess({ reason }: { reason: NoAccessReason }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const requirement = REQUIREMENT[reason];
  const nextStep = NEXT_STEP[reason === 'superadmin' || reason === 'fleet' ? 'platform' : 'tenant'];
  const portalUrl = typeof window !== 'undefined' ? derivePortalUrl(window.location) : null;

  return (
    <section className="space-y-6" aria-labelledby="no-access-title">
      <div>
        <h1
          id="no-access-title"
          className="flex items-center gap-3 text-2xl font-bold text-gray-900 dark:text-white"
        >
          <ShieldOff className="h-7 w-7 text-[#0077BE]" aria-hidden="true" />
          {t('errors.noAccess.title', 'Keine Berechtigung')}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          {t('errors.noAccess.description', 'Ihre Rolle darf die Seite {{path}} nicht öffnen.', { path: pathname })}{' '}
          {t(requirement.key, requirement.fallback)}
        </p>
        <p className="mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          {t(nextStep.key, nextStep.fallback)}
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0077BE] px-4 py-2 text-sm font-medium text-white hover:bg-[#00629c]"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('errors.pageNotFound.backToDashboard', 'Zum Dashboard')}
        </Link>
        {portalUrl ? (
          <a
            href={portalUrl}
            className="inline-flex items-center gap-2 rounded-lg border border-[#0077BE] px-4 py-2 text-sm font-medium text-[#0077BE] hover:bg-[#0077BE]/10"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            {t('errors.noAccess.toPortal', 'Zum Portal')}
          </a>
        ) : null}
      </div>
    </section>
  );
}
